import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile as updateFirebaseProfile,
  User as FirebaseUser,
} from 'firebase/auth';
import { ref, set, get, update, child } from 'firebase/database';
import { auth, database, googleProvider, isFirebaseConfigured } from '../config/firebase';
import { FirebaseUserData, UserRole } from '../types';
import { realtimeDbService as dbService } from './realtimeDbService';

const LOCAL_USER_STORE_KEY = 'adiosoft_auth_users_v2';
const CURRENT_USER_SESSION_KEY = 'adiosoft_current_auth_uid';

// Authoritative Super Admin email - strictly only admin@adiosoft.com
const ADMIN_EMAILS = [
  'admin@adiosoft.com',
];

export const getTurkishAuthErrorMessage = (error: any): string => {
  if (!error) return 'Bilinmeyen bir hata oluştu.';
  const code = error.code || '';
  const message = error.message || '';

  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'E-posta adresi veya şifre hatalı. Lütfen bilgilerinizi kontrol edin.';
    case 'auth/email-already-in-use':
      return 'Bu e-posta adresi ile zaten kayıtlı bir hesap bulunmaktadır. Giriş yapmayı deneyin.';
    case 'auth/weak-password':
      return 'Şifreniz çok zayıf. Lütfen en az 6 karakterden oluşan güçlü bir şifre belirleyin.';
    case 'auth/invalid-email':
      return 'Geçerli bir e-posta adresi giriniz.';
    case 'auth/user-disabled':
      return 'Bu kullanıcı hesabı yönetici tarafından devre dışı bırakılmıştır.';
    case 'auth/too-many-requests':
      return 'Çok fazla başarısız deneme yapıldı. Lütfen birkaç dakika sonra tekrar deneyin.';
    case 'auth/popup-closed-by-user':
      return 'Google ile giriş penceresi kapatıldı.';
    case 'auth/popup-blocked':
      return 'Tarayıcınız açılır pencereyi engelledi. Lütfen izin verip tekrar deneyin.';
    case 'auth/network-request-failed':
      return 'Ağ bağlantı hatası oluştu. Lütfen internet bağlantınızı kontrol edin.';
    case 'auth/operation-not-allowed':
      return 'Bu giriş yöntemi şu anda kullanılamıyor. Lütfen sistem yöneticisiyle iletişime geçin.';
    case 'auth/requires-recent-login':
      return 'Bu işlem için yeniden giriş yapmanız gerekmektedir.';
    default:
      if (message.includes('API key')) {
        return 'Güvenlik altyapısı yapılandırması devam ediyor. Lütfen birazdan tekrar deneyin.';
      }
      return message || 'Kimlik doğrulama işlemi sırasında bir hata oluştu.';
  }
};

export function clearAllSessionAndTenantStorage(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith('adiosoft_') ||
          key.startsWith('firebase:authUser:') ||
          key.includes('tenant') ||
          key.includes('role') ||
          key.includes('admin') ||
          key.includes('current_auth') ||
          key.includes('website'))
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
    sessionStorage.clear();
  } catch (e) {
    console.warn('Storage purge warning:', e);
  }
}

class FirebaseAuthService {
  private getLocalUsers(): Record<string, FirebaseUserData> {
    try {
      const data = localStorage.getItem(LOCAL_USER_STORE_KEY);
      return data ? JSON.parse(data) : {};
    } catch {
      return {};
    }
  }

  private setLocalUsers(users: Record<string, FirebaseUserData>): void {
    try {
      localStorage.setItem(LOCAL_USER_STORE_KEY, JSON.stringify(users));
    } catch (e) {
      console.warn('Local user storage write warning:', e);
    }
  }

  // Get User Profile from Realtime Database / Firestore / Local Cache
  public async getUserProfile(uid: string): Promise<FirebaseUserData | null> {
    if (!uid) return null;

    // 1. Try Realtime Database first if available
    if (database) {
      try {
        const userRef = ref(database, `users/${uid}`);
        const snapshot = await get(userRef);
        if (snapshot.exists()) {
          const val = snapshot.val() as FirebaseUserData;
          this.cacheUserData(val);
          return val;
        }
      } catch (err) {
        console.warn('Realtime Database read error, checking fallbacks:', err);
      }
    }

    // 2. Fallback to Local Storage cache
    const localUsers = this.getLocalUsers();
    return localUsers[uid] || null;
  }

  // Save User Profile to /users/{uid} in Realtime Database
  public async saveUserProfile(userData: FirebaseUserData): Promise<FirebaseUserData> {
    this.cacheUserData(userData);

    // Save to Realtime Database (/users/{uid})
    if (database) {
      try {
        const userRef = ref(database, `users/${userData.uid}`);
        await set(userRef, userData);
      } catch (err) {
        console.warn('Realtime Database save error:', err);
      }
    }

    return userData;
  }

  // Update specific fields of User Profile
  public async updateUserProfile(
    uid: string,
    updates: Partial<FirebaseUserData>
  ): Promise<FirebaseUserData | null> {
    const existing = await this.getUserProfile(uid);
    if (!existing) return null;

    const updatedData: FirebaseUserData = {
      ...existing,
      ...updates,
      updatedAt: Date.now(),
    };

    this.cacheUserData(updatedData);

    if (database) {
      try {
        const userRef = ref(database, `users/${uid}`);
        await update(userRef, { ...updates, updatedAt: Date.now() });
      } catch (err) {
        console.warn('Realtime Database update error:', err);
      }
    }

    return updatedData;
  }

  private cacheUserData(userData: FirebaseUserData): void {
    const localUsers = this.getLocalUsers();
    localUsers[userData.uid] = userData;
    this.setLocalUsers(localUsers);
  }

  // Determine initial role for user - STRICT: Only admin@adiosoft.com can be super_admin
  private resolveRole(email?: string | null): 'customer' | 'admin' | 'super_admin' {
    if (!email) return 'customer';
    const clean = email.toLowerCase().trim();
    if (clean === 'admin@adiosoft.com') {
      return 'super_admin';
    }
    return 'customer';
  }

  // 1. Register with Email and Password
  public async registerWithEmail(
    email: string,
    password: string,
    displayName: string,
    companyName?: string,
    sector: string = 'CORPORATE'
  ): Promise<{ user: FirebaseUser; userData: FirebaseUserData }> {
    const trimmedEmail = email.trim().toLowerCase();
    const role = this.resolveRole(trimmedEmail);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, trimmedEmail, password);
      const user = userCredential.user;

      // Update Firebase Auth profile display name
      if (displayName) {
        await updateFirebaseProfile(user, { displayName });
      }

      // Send email verification link
      try {
        await sendEmailVerification(user);
      } catch (verifyErr) {
        console.warn('Email verification send notice:', verifyErr);
      }

      // Create /users/{uid} user record in Realtime Database
      const now = Date.now();
      const trialDurationMs = 3 * 24 * 60 * 60 * 1000; // 72 hours
      const userData: FirebaseUserData = {
        uid: user.uid,
        email: user.email || trimmedEmail,
        displayName: displayName || user.displayName || 'Müşteri',
        photoURL: user.photoURL || null,
        role: role,
        status: 'active',
        createdAt: now,
        updatedAt: now,
        lastLoginAt: now,
        companyName: companyName || '',
        sector: sector,
        emailVerified: user.emailVerified,
        trialStartDate: now,
        trialEndDate: now + trialDurationMs,
        trialStatus: 'ACTIVE',
        trialSector: null,
      };

      await this.saveUserProfile(userData);
      await dbService.saveUserTrial(user.uid, {
        trialStartDate: now,
        trialEndDate: now + trialDurationMs,
        trialStatus: 'ACTIVE',
        websiteLimit: 1,
        used: 0,
        sector: null,
        createdAt: now,
        updatedAt: now,
      });
      localStorage.setItem(CURRENT_USER_SESSION_KEY, user.uid);

      return { user, userData };
    } catch (error: any) {
      // If Firebase credentials are demo/offline, support seamless registration experience
      if (!isFirebaseConfigured || error.code === 'auth/api-key-not-valid' || error.message?.includes('API key')) {
        const dummyUid = `usr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
        const now = Date.now();
        const trialDurationMs = 3 * 24 * 60 * 60 * 1000;
        const dummyUserData: FirebaseUserData = {
          uid: dummyUid,
          email: trimmedEmail,
          displayName: displayName || 'Müşteri',
          photoURL: null,
          role: role,
          status: 'active',
          createdAt: now,
          updatedAt: now,
          lastLoginAt: now,
          companyName: companyName || '',
          sector: sector,
          emailVerified: true,
          trialStartDate: now,
          trialEndDate: now + trialDurationMs,
          trialStatus: 'ACTIVE',
          trialSector: null,
        };
        await this.saveUserProfile(dummyUserData);
        await dbService.saveUserTrial(dummyUid, {
          trialStartDate: now,
          trialEndDate: now + trialDurationMs,
          trialStatus: 'ACTIVE',
          websiteLimit: 1,
          used: 0,
          sector: null,
          createdAt: now,
          updatedAt: now,
        });
        localStorage.setItem(CURRENT_USER_SESSION_KEY, dummyUid);
        return {
          user: {
            uid: dummyUid,
            email: trimmedEmail,
            displayName: displayName,
            photoURL: null,
            emailVerified: true,
          } as any,
          userData: dummyUserData,
        };
      }
      throw error;
    }
  }

  // 2. Login with Email and Password
  public async loginWithEmail(
    email: string,
    password: string
  ): Promise<{ user: FirebaseUser; userData: FirebaseUserData }> {
    const trimmedEmail = email.trim().toLowerCase();

    try {
      const userCredential = await signInWithEmailAndPassword(auth, trimmedEmail, password);
      const user = userCredential.user;

      let userData = await this.getUserProfile(user.uid);
      const now = Date.now();

      if (!userData) {
        // Create user record if missing
        userData = {
          uid: user.uid,
          email: user.email || trimmedEmail,
          displayName: user.displayName || trimmedEmail.split('@')[0],
          photoURL: user.photoURL || null,
          role: this.resolveRole(trimmedEmail),
          status: 'active',
          createdAt: now,
          updatedAt: now,
          lastLoginAt: now,
          emailVerified: user.emailVerified,
        };
        await this.saveUserProfile(userData);
      } else {
        // Update last login
        userData = await this.updateUserProfile(user.uid, {
          lastLoginAt: now,
          emailVerified: user.emailVerified,
        }) as FirebaseUserData;
      }

      localStorage.setItem(CURRENT_USER_SESSION_KEY, user.uid);
      return { user, userData };
    } catch (error: any) {
      // Demo / fallback handling if unconfigured
      if (!isFirebaseConfigured || error.code === 'auth/api-key-not-valid' || error.message?.includes('API key')) {
        const localUsers = this.getLocalUsers();
        let matched = Object.values(localUsers).find((u) => u.email && u.email.toLowerCase() === trimmedEmail);
        const now = Date.now();

        if (!matched) {
          const dummyUid = `usr_demo_${Date.now().toString(36)}`;
          matched = {
            uid: dummyUid,
            email: trimmedEmail,
            displayName: trimmedEmail.split('@')[0],
            photoURL: null,
            role: this.resolveRole(trimmedEmail),
            status: 'active',
            createdAt: now,
            updatedAt: now,
            lastLoginAt: now,
            companyName: 'Örnek İşletme',
            emailVerified: true,
          };
        } else {
          matched.lastLoginAt = now;
        }

        await this.saveUserProfile(matched);
        localStorage.setItem(CURRENT_USER_SESSION_KEY, matched.uid);

        return {
          user: {
            uid: matched.uid,
            email: matched.email,
            displayName: matched.displayName,
            photoURL: matched.photoURL,
            emailVerified: true,
          } as any,
          userData: matched,
        };
      }
      throw error;
    }
  }

  // 3. Login with Google (Popup)
  public async loginWithGoogle(): Promise<{ user: FirebaseUser; userData: FirebaseUserData }> {
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      const user = userCredential.user;
      const now = Date.now();

      let userData = await this.getUserProfile(user.uid);

      if (!userData) {
        userData = {
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || 'Google Kullanıcısı',
          photoURL: user.photoURL || null,
          role: this.resolveRole(user.email || ''),
          status: 'active',
          createdAt: now,
          updatedAt: now,
          lastLoginAt: now,
          emailVerified: user.emailVerified,
        };
        await this.saveUserProfile(userData);
      } else {
        userData = await this.updateUserProfile(user.uid, {
          lastLoginAt: now,
          displayName: user.displayName || userData.displayName,
          photoURL: user.photoURL || userData.photoURL,
          emailVerified: user.emailVerified,
        }) as FirebaseUserData;
      }

      localStorage.setItem(CURRENT_USER_SESSION_KEY, user.uid);
      return { user, userData };
    } catch (error: any) {
      if (!isFirebaseConfigured || error.code === 'auth/api-key-not-valid' || error.message?.includes('API key')) {
        const dummyUid = `usr_google_${Date.now().toString(36)}`;
        const now = Date.now();
        const dummyUserData: FirebaseUserData = {
          uid: dummyUid,
          email: 'google.user@example.com',
          displayName: 'Google Kullanıcısı',
          photoURL: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=200&auto=format&fit=crop',
          role: 'customer',
          status: 'active',
          createdAt: now,
          updatedAt: now,
          lastLoginAt: now,
          emailVerified: true,
        };
        await this.saveUserProfile(dummyUserData);
        localStorage.setItem(CURRENT_USER_SESSION_KEY, dummyUid);
        return {
          user: {
            uid: dummyUid,
            email: dummyUserData.email,
            displayName: dummyUserData.displayName,
            photoURL: dummyUserData.photoURL,
            emailVerified: true,
          } as any,
          userData: dummyUserData,
        };
      }
      throw error;
    }
  }

  // 4. Send Password Reset Email
  public async sendPasswordReset(email: string): Promise<void> {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      throw new Error('Lütfen geçerli bir e-posta adresi girin.');
    }

    try {
      await sendPasswordResetEmail(auth, trimmedEmail);
    } catch (error: any) {
      if (!isFirebaseConfigured || error.code === 'auth/api-key-not-valid' || error.message?.includes('API key')) {
        // Mock success in dev/demo mode
        console.log(`[Demo] Password reset email sent to: ${trimmedEmail}`);
        return;
      }
      throw error;
    }
  }

  // 5. Send Email Verification
  public async sendEmailVerification(user?: FirebaseUser | null): Promise<void> {
    const targetUser = user || auth.currentUser;
    if (!targetUser) {
      throw new Error('Doğrulama e-postası göndermek için oturum açılmış olmalıdır.');
    }

    try {
      await sendEmailVerification(targetUser);
    } catch (error: any) {
      if (!isFirebaseConfigured || error.code === 'auth/api-key-not-valid' || error.message?.includes('API key')) {
        console.log(`[Demo] Verification email sent to: ${targetUser.email}`);
        return;
      }
      throw error;
    }
  }

  // 6. Sign Out / Logout
  public async logout(): Promise<void> {
    try {
      await signOut(auth);
    } catch (err) {
      console.warn('Sign out warning:', err);
    }
    clearAllSessionAndTenantStorage();
  }

  // Helper for quick Demo Account Switching
  public async demoLogin(type: 'corporate' | 'yacht' | 'admin'): Promise<FirebaseUserData> {
    const now = Date.now();
    let demoData: FirebaseUserData;

    if (type === 'admin') {
      demoData = {
        uid: 'usr-superadmin',
        email: 'admin@adiosoft.com',
        displayName: 'Platform Admin',
        photoURL: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=200&auto=format&fit=crop',
        role: 'admin',
        status: 'active',
        createdAt: now - 86400000 * 60,
        updatedAt: now,
        lastLoginAt: now,
        companyName: 'Adiosoft Technologies Inc.',
        phone: '+90 850 800 00 00',
        emailVerified: true,
      };
    } else if (type === 'yacht') {
      demoData = {
        uid: 'usr-yacht-owner',
        email: 'kaptan@aegeanazure.com',
        displayName: 'Kaptan Deniz Eren',
        photoURL: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=200&auto=format&fit=crop',
        role: 'customer',
        status: 'active',
        createdAt: now - 86400000 * 20,
        updatedAt: now,
        lastLoginAt: now,
        companyName: 'Aegean Azure Yachting',
        sector: 'YACHT_RENTAL',
        phone: '+90 542 200 30 40',
        emailVerified: true,
      };
    } else {
      demoData = {
        uid: 'usr-corp-owner',
        email: 'ahmet@apexholding.com',
        displayName: 'Ahmet Yılmaz',
        photoURL: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=200&auto=format&fit=crop',
        role: 'customer',
        status: 'active',
        createdAt: now - 86400000 * 30,
        updatedAt: now,
        lastLoginAt: now,
        companyName: 'Apex Holding A.Ş.',
        sector: 'CORPORATE',
        phone: '+90 532 100 20 30',
        emailVerified: true,
      };
    }

    await this.saveUserProfile(demoData);
    localStorage.setItem(CURRENT_USER_SESSION_KEY, demoData.uid);
    return demoData;
  }
}

export const firebaseAuthService = new FirebaseAuthService();
